import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { irrigationInfraApi, prefsApi, type PlantEmitter } from '../../lib/api'

// EmitterEditor — manage a plant's irrigation emitters (drip / bubbler / sprinkler …) and a
// local "irrigation on/off" toggle (a mature tree can stay in its zone but have its emitter
// shut off). Same physical location as the plant; this is the irrigation concern (§172/§173).
// Flow is stored canonically in GPM; the user can enter GPM or LPM.

const TYPES = ['drip', 'bubbler', 'sprinkler', 'micro_spray', 'soaker', 'other'] as const
const LPM_PER_GPM = 3.78541
const C = {
  text: 'var(--color-text-primary)',
  muted: 'var(--color-text-secondary)',
  border: 'var(--color-border, #2a3320)',
  card: 'var(--color-background-secondary, #1a2212)',
  amber: 'var(--color-primary, #F5A623)',
}

export function EmitterEditor({ plantId, irrigationEnabled = true }: { plantId: string; irrigationEnabled?: boolean }) {
  const [emitters, setEmitters] = useState<PlantEmitter[]>([])
  const [loading, setLoading] = useState(true)
  const [enabled, setEnabled] = useState(irrigationEnabled)
  const [unit, setUnit] = useState<'gpm' | 'lpm'>('gpm')
  const [unitTouched, setUnitTouched] = useState(false)
  // new-emitter form
  const [type, setType] = useState<string>('drip')
  const [flow, setFlow] = useState('')
  const [count, setCount] = useState('1')

  // Default the flow unit to the tenant measurement system (metric → LPM, imperial → GPM)
  // until the user manually toggles it.
  const { data: prefs } = useQuery({ queryKey: ['user-preferences'], queryFn: () => prefsApi.get().then(r => r.data) })
  useEffect(() => {
    if (!unitTouched && prefs) setUnit(prefs.measurement_system === 'metric' ? 'lpm' : 'gpm')
  }, [prefs, unitTouched])

  useEffect(() => { setEnabled(irrigationEnabled) }, [irrigationEnabled])
  useEffect(() => {
    let live = true
    irrigationInfraApi.listEmitters(plantId)
      .then(r => { if (live) setEmitters(r.data.emitters || []) })
      .catch(() => {})
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [plantId])

  const toGPM = (v: number) => (unit === 'lpm' ? v / LPM_PER_GPM : v)
  const showFlow = (gpm?: number | null) => {
    if (gpm == null) return '—'
    return unit === 'lpm' ? `${(gpm * LPM_PER_GPM).toFixed(2)} LPM` : `${gpm.toFixed(2)} GPM`
  }

  async function add() {
    const f = parseFloat(flow)
    const c = parseInt(count || '1', 10)
    const body: Partial<PlantEmitter> = { plant_id: plantId, emitter_type: type, count: c > 0 ? c : 1 }
    if (!isNaN(f) && f > 0) body.gpm = toGPM(f)
    try {
      const r = await irrigationInfraApi.createEmitter(body)
      setEmitters(e => [...e, r.data])
      setFlow(''); setCount('1')
    } catch { /* ignore */ }
  }
  async function remove(id: string) {
    try { await irrigationInfraApi.deleteEmitter(id); setEmitters(e => e.filter(x => x.id !== id)) } catch { /* ignore */ }
  }
  async function toggleIrrigation() {
    const next = !enabled
    setEnabled(next)
    try { await irrigationInfraApi.setPlantIrrigation(plantId, next) } catch { setEnabled(!next) }
  }

  const inputStyle: React.CSSProperties = {
    padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.text, fontSize: 13,
  }

  return (
    <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Irrigation emitters</span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', border: `1px solid ${C.border}`, borderRadius: 6, overflow: 'hidden' }}>
          {(['gpm', 'lpm'] as const).map(u => (
            <button key={u} onClick={() => { setUnit(u); setUnitTouched(true) }} type="button"
              style={{ padding: '3px 8px', fontSize: 11, cursor: 'pointer', border: 'none',
                background: unit === u ? C.amber : 'transparent', color: unit === u ? '#0E1209' : C.muted, fontWeight: 600 }}>
              {u.toUpperCase()}
            </button>
          ))}
        </span>
      </div>

      {/* Per-tree irrigation on/off */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.text, marginBottom: 10, cursor: 'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={toggleIrrigation} />
        Irrigation active for this tree
        {!enabled && <span style={{ fontSize: 11, color: C.amber }}>· shut off (excluded from the zone's water demand)</span>}
      </label>

      {loading ? (
        <div style={{ fontSize: 12, color: C.muted }}>Loading emitters…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {emitters.map(e => (
            <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, background: C.card }}>
              <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{e.emitter_type.replace('_', ' ')}</span>
              <span style={{ color: C.muted }}>×{e.count}</span>
              <span style={{ color: C.muted }}>· {showFlow(e.gpm)}{e.gpm != null && e.count > 1 ? ` ea (${showFlow((e.gpm) * e.count)} total)` : ''}</span>
              <button onClick={() => remove(e.id)} type="button" style={{ marginLeft: 'auto', fontSize: 11, color: '#EF4444', background: 'transparent', border: 'none', cursor: 'pointer' }}>Remove</button>
            </div>
          ))}
          {emitters.length === 0 && <div style={{ fontSize: 12, color: C.muted }}>No emitters yet.</div>}
        </div>
      )}

      {/* Add emitter */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10, alignItems: 'center' }}>
        <select value={type} onChange={e => setType(e.target.value)} style={inputStyle}>
          {TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
        </select>
        <input value={flow} onChange={e => setFlow(e.target.value)} placeholder={`flow (${unit.toUpperCase()})`} inputMode="decimal" style={{ ...inputStyle, width: 110 }} />
        <input value={count} onChange={e => setCount(e.target.value)} placeholder="count" inputMode="numeric" style={{ ...inputStyle, width: 64 }} />
        <button onClick={add} type="button" style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: C.amber, color: '#0E1209', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>+ Add</button>
      </div>
    </div>
  )
}
